import { mayLeavePage } from "../navigation/leaveGuard";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Image,
  Skeleton,
  useColorMode,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  ComponentType,
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import {
  Link as RouterLink,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { assets } from "../../assets/assets";
import { ChooseEventDepartmentModal } from "../departments/ChooseEventDepartmentModal";
import { useCreateSourcingEvent } from "../departments/useCreateSourcingEvent";
import { useCreateMarketDialogue } from "../market-dialogues/useCreateMarketDialogue";
import { useCreateProcurementWithAi } from "../agent/useCreateProcurementWithAi";
import { urls } from "../../urls";
import { useCreateNeed } from "../needs/useCreateNeed";
import { AssistantPanelTrigger } from "../agent/AssistantPanelTrigger";
import { AssistantPanelContext } from "../agent/assistantPanelContext";
import type { SubAccess } from "../subscription/useSub";
import { navRoleFor, useNavAccess } from "./useNavAccess";
import { NotificationBell } from "../notifications/NotificationBell";
import type {
  AppRole,
  NavSection,
  NavSectionAction,
  SectionActionKind,
} from "./navConfig";
import { navConfig } from "./navConfig";
import { isNavItemActive } from "./navActive";
import { SidebarSection } from "./SidebarSection";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { HelpButton } from "../support/help/HelpButton";
import { useMessengerBesideMenu } from "../support/help/useMessengerBesideMenu";
import { SIDEBAR_ICON_WIDTH, SidebarIconRail } from "./SidebarIconRail";
import { MENU_WIDTH_PX, useSidebarMode } from "./sidebarMode";
import { menuForcedToIcons, usePanelLayout } from "./panelLayoutContext";
import { Tooltip } from "../components/Tooltip";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useKeyboardModality } from "../hooks/useKeyboardModality";
import { useCollapsedSections } from "./useCollapsedSections";
import { NAV_REGION_ID } from "./useRegionToggle";
import { requestContentFocus } from "./useFocusFirstRow";
import type { NavBadges } from "./useNavBadges";
import { isFullAssistantPage } from "../agent/fullAssistantPage";

const SIDEBAR_WIDTH = `${MENU_WIDTH_PX.full}px`;
const ANCHORED_TO_VIEWPORT = {
  top: "0",
  right: "0",
  bottom: "0",
  left: "0",
  w: "auto",
  h: "auto",
};

export interface CreateModals {
  CreateContractModal: ComponentType<{ onClose: () => void }>;
  CreateProjectModal: ComponentType<{
    isOpen: boolean;
    setShowModal: Dispatch<SetStateAction<boolean>>;
  }>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  role: AppRole;
  badges: NavBadges;
  createModals: CreateModals;
}

const SidebarContent = ({
  sections,
  role,
  access,
  badges,
  isCollapsed,
  onToggle,
  onNavigate,
  onAction,
  pendingActionKind,
  regionId,
  navRef,
  resolving,
  onHideMenu,
  showsHelp = false,
}: {
  sections: NavSection[];
  role: AppRole;
  access: SubAccess;
  badges: NavBadges;
  isCollapsed: (sectionKey: string) => boolean;
  onToggle: (sectionKey: string) => void;
  onNavigate: (path: string, query?: string) => void;
  onAction: (action: NavSectionAction) => void;
  pendingActionKind: SectionActionKind | null;
  regionId?: string;
  navRef?: RefObject<HTMLDivElement>;
  resolving?: boolean;
  onHideMenu?: () => void;
  showsHelp?: boolean;
}) => {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  return (
    <Flex direction="column" h="full">
      <Flex
        align="center"
        justify="space-between"
        h="60px"
        px="4"
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="smBorder"
      >
        <RouterLink
          to={urls.app.home}
          onClick={(event) => {
            event.preventDefault();
            onNavigate(urls.app.home);
          }}
        >
          <Image
            src={colorMode === "dark" ? assets.logo_dark : assets.logo}
            alt="SourceMagnet"
            h="26px"
          />
        </RouterLink>
        {/* Desktop only: on mobile the (drawer) sidebar is paired with the top bar, which carries the bell. */}
        <Flex display={{ base: "none", lg: "flex" }} align="center">
          <AssistantPanelTrigger />
          <NotificationBell placement="bottom-start" />
        </Flex>
      </Flex>
      <VStack
        as="nav"
        ref={navRef}
        id={regionId}
        tabIndex={-1}
        aria-label={t("Main navigation") ?? undefined}
        outline="none"
        flex="1"
        align="stretch"
        spacing="0"
        px="3"
        py="2"
        overflowY="auto"
      >
        {resolving ? (
          <VStack
            align="stretch"
            spacing="2"
            pt="2"
            aria-busy="true"
            aria-label={t("Loading the menu") ?? undefined}
          >
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} h="8" borderRadius="md" />
            ))}
          </VStack>
        ) : (
          sections.map((section) => (
            <SidebarSection
              key={section.titleKey}
              section={section}
              role={role}
              access={access}
              badges={badges}
              isCollapsed={isCollapsed(section.titleKey)}
              onToggle={onToggle}
              onNavigate={onNavigate}
              onAction={onAction}
              pendingActionKind={pendingActionKind}
            />
          ))
        )}
      </VStack>
      <Box flexShrink={0} borderTop="1px solid" borderColor="smBorder">
        {showsHelp && (
          <Box px="3" pt="2">
            <HelpButton />
          </Box>
        )}
        {onHideMenu && (
          <Box px="3" pt="2">
            <Button
              variant="ghost"
              size="sm"
              w="full"
              justifyContent="flex-start"
              color="smMuted"
              fontWeight="normal"
              leftIcon={<Icon as={FiChevronsLeft} />}
              onClick={onHideMenu}
            >
              {t("Hide menu")}
            </Button>
          </Box>
        )}
        <SidebarUserMenu />
      </Box>
    </Flex>
  );
};

const IconRailContent = ({
  onShowMenu,
  showsHelp,
  children,
}: {
  onShowMenu: () => void;
  showsHelp: boolean;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const panelOpen = useContext(AssistantPanelContext)?.panelOpen ?? false;
  return (
    <Flex direction="column" h="full" align="stretch">
      <Flex
        align="center"
        justify="center"
        h="60px"
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="smBorder"
      >
        <NotificationBell placement="right-start" />
      </Flex>
      {!panelOpen && (
        <Flex justify="center" pt="2" flexShrink={0}>
          <AssistantPanelTrigger />
        </Flex>
      )}
      {children}
      <Box flexShrink={0} borderTop="1px solid" borderColor="smBorder">
        {showsHelp && (
          <Flex justify="center" pt="2">
            <HelpButton compact />
          </Flex>
        )}
        <Flex justify="center" pt="2">
          <Tooltip label={t("Show full menu")} placement="right">
            <IconButton
              variant="ghost"
              size="sm"
              color="smMuted"
              aria-label={t("Show full menu")}
              icon={<Icon as={FiChevronsRight} />}
              onClick={onShowMenu}
            />
          </Tooltip>
        </Flex>
        <SidebarUserMenu compact />
      </Box>
    </Flex>
  );
};

const projectOnPage = (pathname: string): string | undefined =>
  matchPath(urls.app.projects.view.pathname, pathname)?.params.projectId ??
  matchPath(urls.projects.view.pathname, pathname)?.params.projectId;

export const Sidebar = ({
  isOpen,
  onClose,
  role: appRole,
  badges,
  createModals,
}: Props) => {
  const { CreateContractModal, CreateProjectModal } = createModals;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { create: createNeed } = useCreateNeed();
  const createEvent = useCreateSourcingEvent((eventId) =>
    navigate(urls.app.sourcingEvents.edit.go(eventId).info),
  );
  const createMarketDialogue = useCreateMarketDialogue((id) => navigate(urls.app.marketDialogues.view.go(id)));
  const createWithAi = useCreateProcurementWithAi(
    projectOnPage(location.pathname),
  );
  const { access, owned, menuResolving: resolving } = useNavAccess();
  const role = navRoleFor(appRole, owned);
  const [showCreateContractModal, setShowCreateContractModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const usingKeyboard = useKeyboardModality();
  const drawerNavRef = useRef<HTMLDivElement>(null);
  const { me } = useLoggedInWithOrgContextState();
  const sidebarMode = useSidebarMode(me.id);
  const forcedToIcons =
    menuForcedToIcons(usePanelLayout().placement) ||
    isFullAssistantPage(location.pathname, location.search);
  const iconsOnly = sidebarMode.mode === "icons" || forcedToIcons;
  const showsHelp = appRole !== "supplier";
  const menuBesideContent =
    useBreakpointValue({ base: false, lg: true }) ?? false;
  const menuWidthPx = menuBesideContent
    ? parseInt(iconsOnly ? SIDEBAR_ICON_WIDTH : SIDEBAR_WIDTH)
    : 0;
  useMessengerBesideMenu(showsHelp, menuWidthPx);
  const [menuOverPage, setMenuOverPage] = useState(false);
  const showFullMenu = forcedToIcons
    ? () => setMenuOverPage(true)
    : sidebarMode.toggle;
  const closeDrawer = () => {
    setMenuOverPage(false);
    onClose();
  };

  const sections = navConfig.filter(
    (section) => !section.access || access[section.access],
  );
  const activeSectionKey = sections.find((section) =>
    section.items.some((item) =>
      isNavItemActive(item, location.pathname, location.search, section.items),
    ),
  )?.titleKey;
  // Lifted above the two SidebarContent instances (desktop rail and mobile drawer) so folding a
  // section in one is reflected in the other.
  const { isCollapsed, toggle } = useCollapsedSections(
    sections.map((section) => section.titleKey),
    activeSectionKey,
  );

  const onNavigate = (path: string, query?: string) => {
    if (!mayLeavePage()) {
      closeDrawer();
      return;
    }
    if (path === urls.app.needs.new) {
      void createNeed();
      closeDrawer();
      return;
    }
    // Only for the keyboard: a pointer user has their attention where they clicked, and moving
    // focus into the list would scroll the page out from under them.
    if (usingKeyboard()) requestContentFocus();
    navigate(query ? `${path}?${query}` : path);
    closeDrawer();
  };

  const actionHandlers: Record<SectionActionKind, () => void> = {
    createSourcingEvent: () => {
      closeDrawer();
      (createWithAi ?? createEvent.start)();
    },
    createMarketDialogue: () => {
      closeDrawer();
      createMarketDialogue.start();
    },
    createContract: () => setShowCreateContractModal(true),
    createProject: () => setShowCreateProjectModal(true),
  };

  const onAction = (action: NavSectionAction) => {
    if (!mayLeavePage()) {
      closeDrawer();
      return;
    }
    actionHandlers[action.kind]();
    closeDrawer();
  };
  const pendingActionKind: SectionActionKind | null = createEvent.isCreating
    ? "createSourcingEvent"
    : createMarketDialogue.isCreating
      ? "createMarketDialogue"
      : null;

  return (
    <>
      <Box
        as="aside"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        w={iconsOnly ? SIDEBAR_ICON_WIDTH : SIDEBAR_WIDTH}
        flexShrink={0}
        h="full"
        overflow="hidden"
        bg="smBackground"
        borderRight="1px solid"
        borderColor="smBorder"
      >
        {iconsOnly ? (
          <IconRailContent onShowMenu={showFullMenu} showsHelp={showsHelp}>
            <SidebarIconRail
              sections={sections}
              resolving={resolving}
              role={role}
              access={access}
              badges={badges}
              activeSectionKey={activeSectionKey}
              onNavigate={onNavigate}
              onAction={onAction}
              pendingActionKind={pendingActionKind}
              regionId={NAV_REGION_ID}
            />
          </IconRailContent>
        ) : (
          <SidebarContent
            sections={sections}
            role={role}
            access={access}
            badges={badges}
            isCollapsed={isCollapsed}
            onToggle={toggle}
            onNavigate={onNavigate}
            onAction={onAction}
            pendingActionKind={pendingActionKind}
            regionId={NAV_REGION_ID}
            resolving={resolving}
            onHideMenu={sidebarMode.toggle}
            showsHelp={showsHelp}
          />
        )}
      </Box>

      <Drawer
        isOpen={isOpen || menuOverPage}
        placement="left"
        onClose={closeDrawer}
        size="xs"
        initialFocusRef={drawerNavRef}
      >
        {/* Chakra sizes the drawer's overlay, container and panel with 100vh, which iOS browsers miscompute. */}
        <DrawerOverlay {...ANCHORED_TO_VIEWPORT} />
        <DrawerContent
          h="auto"
          maxH="none"
          containerProps={ANCHORED_TO_VIEWPORT}
        >
          <DrawerBody p="0">
            <SidebarContent
              sections={sections}
              role={role}
              access={access}
              badges={badges}
              isCollapsed={isCollapsed}
              onToggle={toggle}
              onNavigate={onNavigate}
              onAction={onAction}
              pendingActionKind={pendingActionKind}
              navRef={drawerNavRef}
              resolving={resolving}
              showsHelp={showsHelp}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {createEvent.isChoosingDepartment && (
        <ChooseEventDepartmentModal
          isOpen
          departments={createEvent.departments}
          isCreating={createEvent.isCreating}
          onCancel={createEvent.cancelChoosingDepartment}
          onConfirm={createEvent.confirmDepartment}
        />
      )}
      {createMarketDialogue.isChoosingDepartment && (
        <ChooseEventDepartmentModal
          isOpen
          departments={createMarketDialogue.departments}
          isCreating={createMarketDialogue.isCreating}
          onCancel={createMarketDialogue.cancelChoosingDepartment}
          onConfirm={createMarketDialogue.confirmDepartment}
          wording={{
            title: t("New market dialogue"),
            question: t("Which department should the market dialogue belong to?"),
            noDepartment: t("You cannot create market dialogues in any department in this organization."),
          }}
        />
      )}
      {showCreateContractModal && (
        <CreateContractModal
          onClose={() => setShowCreateContractModal(false)}
        />
      )}
      {showCreateProjectModal && (
        <CreateProjectModal isOpen setShowModal={setShowCreateProjectModal} />
      )}
    </>
  );
};
